import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {Helmet} from 'react-helmet'
import type {AppLocale} from 'i18n/locales'
import {isAppLocale, resolveInitialLocale, writeCachedLocale} from 'i18n/locales'
import {withLocalePath} from 'i18n/localePaths'
import {routes} from 'routes/routes'
import {normalizePromoInviteToken} from 'organization/promoInviteToken'
import {
  PromoOfferLongPage,
  type PromoOfferEconomy,
} from 'attendee/promoOffer/PromoOfferLongPage'
import {usePromoOfferCopy} from 'attendee/promoOffer/usePromoOfferCopy'

type PromoApiPayload = {
  slug: string
  name: string
  economy: PromoOfferEconomy
  invite?: {
    expires_at: string
  } | null
}

export function PromoPackageLanding() {
  const {slug = ''} = useParams<{slug: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const inviteFromUrl = useMemo(
    () => normalizePromoInviteToken(searchParams.get('invite')),
    [searchParams],
  )

  const langParam = searchParams.get('lang')
  const [pageLocale, setPageLocaleState] = useState<AppLocale>(() => {
    if (isAppLocale(langParam || undefined)) {
      return langParam as AppLocale
    }
    return resolveInitialLocale()
  })

  useEffect(() => {
    if (isAppLocale(langParam || undefined) && langParam !== pageLocale) {
      setPageLocaleState(langParam as AppLocale)
    }
  }, [langParam, pageLocale])

  const setPageLocale = useCallback(
    (l: AppLocale) => {
      setPageLocaleState(l)
      writeCachedLocale(l)
      const next = new URLSearchParams(searchParams)
      next.set('lang', l)
      setSearchParams(next, {replace: true})
    },
    [searchParams, setSearchParams],
  )

  const [data, setData] = useState<PromoApiPayload | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') ?? ''
        const qs = new URLSearchParams()
        if (inviteFromUrl) {
          qs.set('invite', inviteFromUrl)
        }
        const q = qs.toString()
        const res = await fetch(
          `${base}/api/promo-packages/${encodeURIComponent(slug)}${q ? `?${q}` : ''}`,
        )
        if (!res.ok) {
          throw new Error('not found')
        }
        const json = (await res.json()) as PromoApiPayload
        if (!cancelled) setData(json)
      } catch {
        if (!cancelled) setErr('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug, inviteFromUrl])

  const {t} = usePromoOfferCopy(pageLocale)

  if (err) {
    return (
      <div style={{padding: 40, textAlign: 'center'}}>
        <p>Промопакетът не е намерен.</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div style={{padding: 40, textAlign: 'center'}}>
        <p>…</p>
      </div>
    )
  }

  const offerActive = Boolean(data.invite?.expires_at)
  const regPath = withLocalePath(pageLocale, routes.organization.register)
  const registerWithInviteTo =
    offerActive && inviteFromUrl
      ? `${regPath}?invite=${encodeURIComponent(inviteFromUrl)}`
      : null

  return (
    <>
      <Helmet>
        <title>{data.name} | Eventa.tv</title>
        <meta name="description" content={t('offer.metaTitle')} />
        <meta name="robots" content="index,follow" />
      </Helmet>
      <PromoOfferLongPage
        offerName={data.name}
        pageLocale={pageLocale}
        setPageLocale={setPageLocale}
        economy={data.economy}
        inviteFromUrl={inviteFromUrl}
        inviteExpiresAt={data.invite?.expires_at ?? null}
        registerWithInviteTo={registerWithInviteTo}
      />
    </>
  )
}
